import { MediaPrior } from '../data-qa-types';
import { isMeridianPrior } from '../prior-utils';
import { PriorCard } from './PriorCard';

interface InformativePriorsProps {
  priors: MediaPrior[];
}

// Only priors that conform to the Meridian schema can be charted. Legacy pre-Meridian
// priors are ignored (per the econ team); when none remain, InformativePriorsCard
// shows a "not available" message instead of curves.
const renderableOf = (priors: MediaPrior[]): MediaPrior[] => priors.filter(isMeridianPrior);

// The descriptive card (heading/lede/explanation or "not available" fallback) — split
// from InformativePriorsGrid so PriorsStageTabs can put its tab switcher between the
// two: card, then tabs, then the chart grid the switcher actually controls.
export const InformativePriorsCard = ({ priors }: InformativePriorsProps) => {
  const renderablePriors = renderableOf(priors);

  return (
    <div className='data-qa-about__card data-qa-about__card--prose'>
      <h2 className='data-qa-about__heading'>Informative Priors</h2>
      <p className='data-qa-about__lede'>
        Distributions used to initialize the model&apos;s media coefficients.
      </p>
      {renderablePriors.length > 0 ? (
        <ul className='data-qa-about__list data-qa-about__list--muted'>
          <li>
            This section is informational — it describes the priors that will be used to initialize
            the model, not data quality issues to flag.
          </li>
          <li>
            For each paid-media feature, the chart shows the shape of the prior on the modeled
            coefficient (e.g. ROI for Meridian).
          </li>
          <li>
            Tighter distributions reflect stronger priors (more constrained); wider distributions
            are weaker priors.
          </li>
        </ul>
      ) : (
        <p className='data-qa-about__note'>
          Priors are not available for this model, please contact the econ team if you would like
          further details.
        </p>
      )}
    </div>
  );
};

// The chart grid a PriorsStageTabs tab switcher actually controls — renders nothing
// when there's nothing renderable, so it never leaves a stray empty container.
export const InformativePriorsGrid = ({ priors }: InformativePriorsProps) => {
  const renderablePriors = renderableOf(priors);
  if (renderablePriors.length === 0) return null;

  return (
    <div className='data-qa-about__priors'>
      {renderablePriors.map(prior => (
        <PriorCard key={prior.feature} prior={prior} />
      ))}
    </div>
  );
};

// The single-stage (non-tabbed) case: card immediately followed by its own grid.
export const InformativePriors = ({ priors }: InformativePriorsProps) => (
  <>
    <InformativePriorsCard priors={priors} />
    <InformativePriorsGrid priors={priors} />
  </>
);
